import cloneDeep from 'lodash/cloneDeep';
import type { CertificationDocument } from 'src/types/CertificationDocumentTypes';
import { DOCUMENT_POSITION } from './FileManagerV3Utils';

export const compareNumbers = (
  a?: number | null,
  b?: number | null
): number => {
  return (a ?? 0) - (b ?? 0);
};

/** Stable comparator for root-level docs: order first, then id for tiebreaker. */
export const compareRootDocOrder = (
  a: { order?: number | null; id?: number },
  b: { order?: number | null; id?: number }
): number => {
  const byOrder = compareNumbers(a.order, b.order);
  if (byOrder !== 0) {
    return byOrder;
  }
  return (a.id ?? 0) - (b.id ?? 0);
};

export const normalizePosition = (position: unknown): DOCUMENT_POSITION => {
  if (position === DOCUMENT_POSITION.UPPER || position === 'upper') {
    return DOCUMENT_POSITION.UPPER;
  }
  if (position === DOCUMENT_POSITION.LOWER || position === 'lower') {
    return DOCUMENT_POSITION.LOWER;
  }
  return DOCUMENT_POSITION.NONE;
};

export const ensureChildBuckets = (doc: CertificationDocument): void => {
  if (!Array.isArray(doc.upper_children_certification_documents)) {
    doc.upper_children_certification_documents = [];
  }
  if (!Array.isArray(doc.lower_children_certification_documents)) {
    doc.lower_children_certification_documents = [];
  }
};

export const removeDocumentById = (
  nodes: CertificationDocument[],
  docId: number
): boolean => {
  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i];
    if (node.id === docId) {
      nodes.splice(i, 1);
      return true;
    }
    ensureChildBuckets(node);
    if (
      removeDocumentById(node.upper_children_certification_documents, docId)
    ) {
      return true;
    }
    if (
      removeDocumentById(node.lower_children_certification_documents, docId)
    ) {
      return true;
    }
  }
  return false;
};

export const findDocumentById = (
  nodes: CertificationDocument[],
  docId: number
): CertificationDocument | null => {
  for (const node of nodes) {
    if (node.id === docId) {
      return node;
    }
    ensureChildBuckets(node);
    const upperMatch = findDocumentById(
      node.upper_children_certification_documents,
      docId
    );
    if (upperMatch) {
      return upperMatch;
    }
    const lowerMatch = findDocumentById(
      node.lower_children_certification_documents,
      docId
    );
    if (lowerMatch) {
      return lowerMatch;
    }
  }
  return null;
};

export const insertDocumentIntoTree = (
  nodes: CertificationDocument[],
  doc: CertificationDocument
): void => {
  const normalizedPosition = normalizePosition(doc.position);
  ensureChildBuckets(doc);

  if (doc.parent == null) {
    nodes.push(doc);
    nodes.sort(compareRootDocOrder);
    return;
  }

  const parentNode = findDocumentById(nodes, doc.parent);
  if (!parentNode) {
    nodes.push(doc);
    nodes.sort(compareRootDocOrder);
    return;
  }

  ensureChildBuckets(parentNode);
  const targetList =
    normalizedPosition === DOCUMENT_POSITION.UPPER
      ? parentNode.upper_children_certification_documents
      : parentNode.lower_children_certification_documents;

  targetList.push(doc);
  targetList.sort((a, b) => compareNumbers(a.sub_order, b.sub_order));
};

export const upsertDocumentsIntoTree = (
  currentDocuments: CertificationDocument[] | null | undefined,
  incomingDocuments: CertificationDocument[]
): CertificationDocument[] => {
  if (!incomingDocuments.length) {
    return Array.isArray(currentDocuments) ? currentDocuments : [];
  }

  const nextDocuments = cloneDeep(currentDocuments ?? []);

  incomingDocuments.forEach((incoming) => {
    const docClone = cloneDeep(incoming);
    ensureChildBuckets(docClone);
    removeDocumentById(nextDocuments, docClone.id);
    insertDocumentIntoTree(nextDocuments, docClone);
  });

  return nextDocuments;
};
